import React, { FC, useCallback, useContext, useState } from 'react';

import { Button, Label, Select, TextInput } from '@gravity-ui/uikit';
import i18n from '@yandex-int/i18n';
import { updateMainpageSettings as updateHomepageSettings } from 'client/api';
import { IconMedium } from 'client/blocks/AppIcons/AppIcons';
import { ErrorBoundary } from 'client/blocks/ErrorBoundary/ErrorBoundary';
import { Field } from 'client/blocks/form/Field/Field';
import { PageLoader } from 'client/blocks/Loader/PageLoader';
import { CurrentAliceAppContext } from 'client/context/aliceAppContext';
import { useFormSettings } from 'client/hooks/useFormSettings';
import { AliceApp, getHomepageFields, HomepageBadgeType } from 'client/model/skill';
import { EDITOR_FIELDS_BADGES, EDITOR_FIELDS_TAGS, removeDuplicatesUnordered } from 'client/utils/editor-fields';
import * as keyset from 'i18n/EditorFields';

import styles from './AliceAppEditorPage.module.scss';

const t = i18n(keyset);

interface AliceAppEditorPageProps {
    disabled: boolean;
}

type AliceAppEditorFieldsProps = {
    disabled: boolean;
    aliceApp: AliceApp;
    refreshAliceApp: () => void;
};

const AliceAppEditorFields: FC<AliceAppEditorFieldsProps> = ({ aliceApp, refreshAliceApp, disabled }) => {
    const { settings, onChange } = useFormSettings({ defaultSettings: getHomepageFields(aliceApp) });
    const [customTag, setCustomTag] = useState('');
    const [selectableTags, setSelectableTags] = useState(
        removeDuplicatesUnordered([...settings.tags, ...EDITOR_FIELDS_TAGS]).filter(Boolean),
    );
    const [isLoading, setIsLoading] = useState(false);

    const onCustomTagAdd = useCallback(() => {
        setSelectableTags([customTag, ...selectableTags]);
        onChange('tags')([...settings.tags, customTag]);
        setCustomTag('');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customTag, settings.tags, selectableTags]);

    const onSave = useCallback(async () => {
        setIsLoading(true);
        try {
            await updateHomepageSettings(aliceApp.id, settings, aliceApp.channel);
            await refreshAliceApp();
        } catch (e) {
            console.error(e);
        } finally {
            setIsLoading(false);
        }
    }, [aliceApp.id, aliceApp.channel, settings, refreshAliceApp]);

    const onBadgeUpdate = (selectedBadges: string[]) => {
        onChange('homepageBadgeTypes')(selectedBadges as HomepageBadgeType[]);
    };

    const handleAddTag = (tag: string) => onChange('tags')([...settings.tags, tag]);
    const handleDeleteTag = (tag: string) => onChange('tags')(settings.tags.filter((x) => x !== tag));

    return (
        <ErrorBoundary withHomepageLink>
            <header className={styles.aliceAppEditorFields__header}>
                <div className={styles.aliceAppEditorFields__header__titleBlock}>
                    <IconMedium imageUrl={aliceApp.logo2?.url} />
                    <h1 className={styles.aliceAppEditorFields__title}>{aliceApp?.draft?.name}</h1>
                </div>
                <Button onClick={onSave} view="action" loading={isLoading} disabled={disabled || isLoading}>
                    {t('save')}
                </Button>
            </header>
            <section className={styles.aliceAppEditorFields__content}>
                <h2 className={styles.aliceAppEditorFields__pageTitle}>{t('headline')}</h2>

                <Field fieldTitle={t('title')}>
                    <TextInput
                        className={styles.aliceAppEditorFields__formContent}
                        value={settings.editorName}
                        onUpdate={onChange('editorName')}
                        placeholder={t('title-placeholder')}
                        size="l"
                        disabled={disabled}
                    />
                </Field>

                <Field fieldTitle={t('description')}>
                    <TextInput
                        className={styles.aliceAppEditorFields__formContent}
                        value={settings.editorDescription}
                        onUpdate={onChange('editorDescription')}
                        placeholder={t('description-placeholder')}
                        size="l"
                        disabled={disabled}
                    />
                </Field>

                <Field fieldTitle={t('badges')}>
                    <Select
                        value={settings.homepageBadgeTypes}
                        onUpdate={onBadgeUpdate}
                        className={styles.aliceAppEditorFields__formContent}
                        options={EDITOR_FIELDS_BADGES.map(({ value, label }) => ({ value, content: label }))}
                        renderSelectedOption={(option) => (
                            <Label
                                className={styles.aliceAppEditorFields__editorBadgeLabel}
                                theme={disabled ? 'clear' : 'info'}
                                size="xs"
                            >
                                {option.content}
                            </Label>
                        )}
                        placeholder={t('badges-placeholder')}
                        size="l"
                        multiple
                        disabled={disabled}
                    />
                </Field>

                <Field fieldTitle={t('tags')}>
                    <div className={styles.aliceAppEditorFields__tagInputContainer}>
                        <TextInput
                            size="l"
                            value={customTag}
                            onUpdate={(value) => {
                                setCustomTag(value.replace(/[^\w]/g, ''));
                            }}
                            disabled={disabled}
                            placeholder={t('new-tag-placeholder')}
                        />
                        <Button size="l" onClick={onCustomTagAdd} disabled={disabled || customTag.length === 0}>
                            {t('add')}
                        </Button>
                    </div>
                    <div className={styles.aliceAppEditorFields__tagContainer}>
                        {selectableTags.map((tag) => {
                            const checked = settings.tags.includes(tag);

                            return (
                                <Label
                                    key={tag}
                                    type={checked ? 'close' : 'default'}
                                    onClick={checked ? undefined : () => handleAddTag(tag)}
                                    onCloseClick={checked ? () => handleDeleteTag(tag) : undefined}
                                    theme={checked ? 'info' : 'clear'}
                                    disabled={disabled}
                                    size="m"
                                >
                                    {tag}
                                </Label>
                            );
                        })}
                    </div>
                </Field>
            </section>
        </ErrorBoundary>
    );
};

export const AliceAppEditorPage: FC<AliceAppEditorPageProps> = ({ disabled }) => {
    const context = useContext(CurrentAliceAppContext);

    if (!context?.aliceApp || !context?.refreshAliceApp) {
        return <PageLoader />;
    }

    return (
        <AliceAppEditorFields
            aliceApp={context.aliceApp}
            refreshAliceApp={context.refreshAliceApp}
            disabled={disabled}
        />
    );
};
